import React, { useMemo } from 'react';
import type { TravelerInformationResponseArgs } from '@this/domain/traveler_information';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import TravelerInfoDisplay, { TRAVELER_DISPLAY_TYPE } from './TravelerInfoDisplay';

interface Props {
  hotelTravelerInfo: TravelerInformationResponseArgs | null;
  flightTravelerInfo: TravelerInformationResponseArgs | null;
  shinkansenTravelerInfo: TravelerInformationResponseArgs | null;
  hasHotelReservation: boolean;
  hasFlightTransport: boolean;
  hasShinkansenTransport: boolean;
}

const ReservationInfoSection = ({
  hotelTravelerInfo,
  flightTravelerInfo,
  shinkansenTravelerInfo,
  hasHotelReservation,
  hasFlightTransport,
  hasShinkansenTransport
}: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const hasNoReservation =
    !(hasHotelReservation && hotelTravelerInfo) &&
    !(hasFlightTransport && flightTravelerInfo) &&
    !(hasShinkansenTransport && shinkansenTravelerInfo);

  if (hasNoReservation) {
    return (
      <div style={styles.collapsible}>
        <div style={styles.collapsibleHeader}>予約情報</div>
        <div style={styles.collapsibleBody}>予約情報がここに表示されます。</div>
      </div>
    );
  }

  return (
    <>
      {hasHotelReservation && hotelTravelerInfo && (
        <div style={styles.collapsible}>
          <div style={styles.collapsibleHeader}>宿泊情報</div>
          <div style={styles.collapsibleBody}>
            <TravelerInfoDisplay travelerInfo={hotelTravelerInfo} displayType={TRAVELER_DISPLAY_TYPE.HOTEL} />
          </div>
        </div>
      )}
      {hasFlightTransport && flightTravelerInfo && (
        <div style={styles.collapsible}>
          <div style={styles.collapsibleHeader}>航空券搭乗情報</div>
          <div style={styles.collapsibleBody}>
            <TravelerInfoDisplay travelerInfo={flightTravelerInfo} displayType={TRAVELER_DISPLAY_TYPE.FLIGHT} />
          </div>
        </div>
      )}
      {hasShinkansenTransport && shinkansenTravelerInfo && (
        <div style={styles.collapsible}>
          <div style={styles.collapsibleHeader}>新幹線搭乗情報</div>
          <div style={styles.collapsibleBody}>
            <TravelerInfoDisplay
              travelerInfo={shinkansenTravelerInfo}
              displayType={TRAVELER_DISPLAY_TYPE.SHINKANSEN}
            />
          </div>
        </div>
      )}
    </>
  );
};

const getStyles = (theme: AITTheme) => ({
  collapsible: {
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '6px',
    marginTop: '12px'
  },
  collapsibleHeader: {
    padding: '10px 12px',
    backgroundColor: theme.contentBgColor,
    fontWeight: 600 as const
  },
  collapsibleBody: {
    padding: '12px',
    color: theme.grayTextColor,
    fontSize: '13px'
  }
});

export default ReservationInfoSection;
